import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import {
  CreateVmConnectionTemplateRequest,
  DriveDestinationPreset,
  GuestOsType,
  VmConnectionDuplicateCheckRequest,
  VmConnectionDuplicateCheckResponse,
  VmConnectionProtocol,
  VmConnectionTemplateDetail,
  VmConnectionTemplateSummary,
} from '../../../../interfaces/vm-connection-templates/vm-connection-template.interface';
import { VmConnectionTemplateService } from '../../../../services/entity-services/vm-connection-template.service';
import { Log } from '../../../../services/logger.service';

/**
 * In-place vertical step-by-step create panel for VM Connection Templates. Used as an
 * embedded overlay inside the standalone management page, the class VM Template attach
 * drawer, and the org-template card attach drawer.
 *
 * The panel performs a debounced live duplicate check against the backend whenever the
 * user toggles a behavioural field, and surfaces a friendly "matches existing — pick instead"
 * banner before any save is attempted. The host component decides whether to also persist an
 * attachment (per-class or per-resource) once the create succeeds — this component only
 * emits the freshly-created detail via {@link created}.
 *
 * Behavioural defaults exactly match the backend spec:
 *   copy=on, paste=on, file-upload=off, recording=off, audio=off, protocol=RDP.
 */
@Component({
  selector: 'app-vm-connection-template-create-panel',
  templateUrl: './vm-connection-template-create-panel.component.html',
  styleUrls: ['./vm-connection-template-create-panel.component.css'],
})
export class VmConnectionTemplateCreatePanelComponent {
  private readonly logger = Log.create(VmConnectionTemplateCreatePanelComponent);

  /** Emitted when the user dismisses the panel (Cancel button or backdrop click). */
  @Output() cancelled = new EventEmitter<void>();

  /**
   * Emitted with the freshly-created template detail once the backend acknowledges the
   * create. The host component decides whether to also persist an attachment (per-class
   * or per-resource) using the returned id.
   */
  @Output() created = new EventEmitter<VmConnectionTemplateDetail>();

  /**
   * Optional CTA the host can wire to "use this existing template instead" when the live
   * duplicate banner is shown. Emits the existing summary so the host can attach without
   * creating a new record.
   */
  @Output() pickExisting = new EventEmitter<VmConnectionTemplateSummary>();

  /** Allow the host to scope the panel header (e.g. "for this class" / "for this template"). */
  @Input() contextLabel: string | null = null;

  // ── Form state — mirrors CreateVmConnectionTemplateRequest ─────────────────
  readonly protocols: VmConnectionProtocol[] = ['Rdp'];
  readonly guestOsOptions: { value: GuestOsType; label: string }[] = [
    { value: 'Agnostic', label: 'Agnostic / Unknown' },
    { value: 'Windows',  label: 'Windows' },
    { value: 'Linux',    label: 'Linux' },
  ];

  templateName = '';
  description  = '';
  protocol: VmConnectionProtocol = 'Rdp';
  allowCopy            = true;
  allowPaste           = true;
  allowFileUpload      = false;
  allowScreenRecording = false;
  allowAudioPlayback   = false;
  allowBackground      = false;
  guestOs: GuestOsType = 'Agnostic';
  driveDestinationPreset: DriveDestinationPreset = 'Default';
  customDrivePath: string = '';

  // ── UI state ────────────────────────────────────────────────────────────────
  isSaving = false;
  isCheckingDuplicate = false;
  errorMessage: string | null = null;
  duplicateExisting: VmConnectionTemplateSummary | null = null;

  // ── Live duplicate check stream ─────────────────────────────────────────────
  /**
   * Pushed every time a behavioural field changes. Debounced + dedup'd before the
   * backend round-trip to avoid one HTTP call per keystroke / toggle.
   */
  private readonly duplicateCheck$ = new Subject<VmConnectionDuplicateCheckRequest>();

  constructor(private readonly templateService: VmConnectionTemplateService) {
    this.duplicateCheck$
      .pipe(
        debounceTime(250),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        switchMap(req => {
          this.isCheckingDuplicate = true;
          return this.templateService.checkDuplicate(req).pipe(
            catchError(err => {
              this.logger.w('Duplicate check failed', err);
              return of<VmConnectionDuplicateCheckResponse>({ isDuplicate: false, existing: null });
            }),
          );
        }),
      )
      .subscribe({
        next: res => {
          this.isCheckingDuplicate = false;
          this.duplicateExisting = res.isDuplicate ? res.existing : null;
        },
      });
    // Prime the initial state so the duplicate banner is accurate from first paint.
    this.scheduleDuplicateCheck();
  }

  /** Called by every behavioural toggle/select; debounced and dedup'd internally. */
  scheduleDuplicateCheck(): void {
    this.duplicateCheck$.next({
      protocol: this.protocol,
      allowCopy: this.allowCopy,
      allowPaste: this.allowPaste,
      allowFileUpload: this.allowFileUpload,
      allowScreenRecording: this.allowScreenRecording,
      allowAudioPlayback: this.allowAudioPlayback,
      allowBackground: this.allowBackground,
      guestOs: this.guestOs,
      driveDestinationPreset: this.driveDestinationPreset,
      customDrivePath: this.driveDestinationPreset === 'CustomPath' ? (this.customDrivePath || null) : null,
    });
  }

  /** Returns true while the form is incomplete or has known issues blocking save. */
  get isSaveDisabled(): boolean {
    return this.isSaving
      || !this.templateName.trim()
      || !!this.duplicateExisting;
  }

  /** Sends the create POST. Emits {@link created} on success; surfaces validation errors. */
  save(): void {
    if (this.isSaveDisabled) return;

    this.isSaving = true;
    this.errorMessage = null;

    const request: CreateVmConnectionTemplateRequest = {
      templateName: this.templateName.trim(),
      description:  this.description.trim(),
      protocol:     this.protocol,
      allowCopy:            this.allowCopy,
      allowPaste:           this.allowPaste,
      allowFileUpload:      this.allowFileUpload,
      allowScreenRecording: this.allowScreenRecording,
      allowAudioPlayback:   this.allowAudioPlayback,
      allowBackground:      this.allowBackground,
      guestOs:              this.guestOs,
      driveDestinationPreset: this.driveDestinationPreset,
      customDrivePath:      this.driveDestinationPreset === 'CustomPath'
                                ? (this.customDrivePath.trim() || null)
                                : null,
    };

    this.templateService.create(request).subscribe({
      next: detail => {
        this.isSaving = false;
        this.created.emit(detail);
      },
      error: err => {
        this.isSaving = false;
        this.errorMessage = this.mapCreateError(err);
      },
    });
  }

  /** Cancels the panel; the host is responsible for un-mounting it. */
  cancel(): void {
    this.cancelled.emit();
  }

  /** Surfaces the "use existing template instead" CTA when the duplicate banner is shown. */
  pickDuplicate(): void {
    if (this.duplicateExisting) {
      this.pickExisting.emit(this.duplicateExisting);
    }
  }

  /** Lookup-table-style mapping of backend validation codes to friendly UI strings. */
  private mapCreateError(err: any): string {
    const code: string | undefined = err?.error?.error;
    return (code && CREATE_ERROR_MESSAGE_MAP[code])
      ?? err?.error?.message
      ?? err?.message
      ?? 'Failed to create template.';
  }
}

const CREATE_ERROR_MESSAGE_MAP: Readonly<Record<string, string>> = {
  DUPLICATE_CONTENT: 'A template with identical configuration already exists. Pick it instead, or change a setting.',
  NAME_TAKEN:        'A template with this name already exists. Choose a different name.',
  NAME_REQUIRED:     'Template name is required.',
  NAME_TOO_LONG:     'Template name must be 100 characters or fewer.',
  NAME_INVALID_FORMAT:
    'Template name must start with an alphanumeric character and contain only letters, digits, spaces, hyphens, or underscores.',
};
